import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link, useNavigate } from 'react-router-dom';
import InvoicePreview from '../components/common/InvoicePreview';
import PayoutSummary from '../components/common/PayoutSummary';
import CreateProjectModal from '../components/common/CreateProjectModal';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency } from '../components/invoices/utils/formatters';
import styles from '../styles/invoiceCreator.module.css';

const DUE_DATE_OPTIONS = [
  { label: 'Today', value: 0 },
  { label: 'Tomorrow', value: 1 },
  { label: 'In 7 days', value: 7 },
  { label: 'In 14 days', value: 14 },
  { label: 'In 30 days', value: 30 },
  { label: 'In 45 days', value: 45 },
  { label: 'In 60 days', value: 60 },
  { label: 'In 90 days', value: 90 },
  { label: 'Custom', value: 'custom' },
];

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const getFutureDateString = (days) => {
    const today = new Date();
    today.setDate(today.getDate() + days);
    return today.toISOString().slice(0, 10);
};


const InvoiceCreator = () => {
  const { jeweller } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Basic form state
  const [form, setForm] = useState({
    uniqueRefNumber: '',
    status: 'draft',
    dueDate: getFutureDateString(30),
    customerInfo: { name: '', email: '' },
    lineItems: [{ description: '', quantity: 1, price: 0 }],
  });

  // Due date selection state
  const [dueDateOption, setDueDateOption] = useState(30);
  const [customDueDate, setCustomDueDate] = useState('');
  
  // Project lookup state  
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  
  const [showPreview, setShowPreview] = useState(false); // Add this state variable

  // Sending state
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Calculate subtotal
  const subtotal = form.lineItems.reduce(
    (sum, item) => sum + (Number(item.quantity) * Number(item.price)),
    0
  );
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId && allProjects.length > 0) {
      const project = allProjects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project);
        console.log('Auto-selected project:', project.projectName);
      }
    }
  }, [searchParams, allProjects]);

  const calculateInvoiceFees = () => {
    if (subtotal === 0) {
      return {
        subtotal: 0,
        commission: 0,
        processingFee: 0,
        totalPlatformFee: 0,
        netPayout: 0,
        breakdown: { commission: { rate: 0, amount: 0 }, processingFee: { rate: 1.8, amount: 0 } }
      };
    }
  
    // Convert to pence for precision
    const currentInvoiceAmountInPence = Math.round(subtotal * 100);
    const projectPreviousTotalInPence = selectedProject?.financials?.totalInvoicedAmount || 0;
    const projectCommissionAlreadyCollectedInPence = selectedProject?.financials?.totalCommissionCollected || 0;
  
    // 1. Calculate tiered commission (your existing logic)
    const newProjectTotalInPence = projectPreviousTotalInPence + currentInvoiceAmountInPence;
    
    // Tiered commission bands (in pence)
    const bands = [
      { min: 0, max: 300000, rate: 0.14 },      // £0-£3K
      { min: 300000, max: 700000, rate: 0.11 }, // £3K-£7K  
      { min: 700000, max: 1500000, rate: 0.08 }, // £7K-£15K
      { min: 1500000, max: Infinity, rate: 0.05 } // £15K+
    ];
    
    let totalCommissionOwedInPence = 0;
    let remainingAmount = newProjectTotalInPence;
    
    for (const band of bands) {
      if (remainingAmount <= 0) break;
      const bandSize = band.max - band.min;
      const amountInThisBand = Math.min(remainingAmount, bandSize);
      // Round each band calculation to avoid floating point accumulation
      totalCommissionOwedInPence += Math.round(amountInThisBand * band.rate);
      remainingAmount -= amountInThisBand;
    }
  
    // 2. Calculate commission for THIS invoice only
    const commissionForThisInvoiceInPence = Math.round(Math.max(0, totalCommissionOwedInPence - projectCommissionAlreadyCollectedInPence));
    
    // 3. Calculate processing fee (1.8%)
    const processingFeeInPence = Math.round(currentInvoiceAmountInPence * 0.018);
    
    // 4. Calculate totals
    const totalPlatformFeeInPence = commissionForThisInvoiceInPence + processingFeeInPence;
    const netPayoutInPence = currentInvoiceAmountInPence - totalPlatformFeeInPence;
  
    // 5. Calculate effective commission rate for display
    const effectiveCommissionRate = newProjectTotalInPence > 0 ? (totalCommissionOwedInPence / newProjectTotalInPence) * 100 : 14.0;
  
    // Return everything in pounds for UI
    return {
      subtotal: subtotal,
      commission: commissionForThisInvoiceInPence / 100,
      processingFee: processingFeeInPence / 100,
      totalPlatformFee: totalPlatformFeeInPence / 100,
      netPayout: netPayoutInPence / 100,
      breakdown: {
        commission: {
          rate: effectiveCommissionRate,
          amount: commissionForThisInvoiceInPence / 100
        },
        processingFee: {
          rate: 1.8,
          amount: processingFeeInPence / 100
        }
      },
      // For backend when creating invoice
      inPence: {
        subtotal: currentInvoiceAmountInPence,
        commission: commissionForThisInvoiceInPence,
        processingFee: processingFeeInPence,
        totalPlatformFee: totalPlatformFeeInPence,
        netPayout: netPayoutInPence
      }
    };
  };

  const fees = calculateInvoiceFees();
  const commission = fees.commission;
  const processingFee = fees.processingFee; 
  const totalPlatformFee = fees.totalPlatformFee;
  const netPayout = fees.netPayout;

  // Firebase functions
  const functions = getFunctions();
  
  
  // Handle due date option change
  const handleDueDateOptionChange = (e) => {
    const value = e.target.value === 'custom' ? 'custom' : Number(e.target.value);
    setDueDateOption(value);
    if (value !== 'custom') {
      setCustomDueDate('');
      const today = new Date();
      const due = addDays(today, value);
      setForm((prev) => ({ ...prev, dueDate: due.toISOString().slice(0, 10) }));
    } else {
      setForm((prev) => ({ ...prev, dueDate: '' }));
    }
  };

  

  // Handle project created from modal
  const handleProjectCreated = (newProject) => {
    console.log('Project created:', newProject);
    setSelectedProject(newProject);
    setSearchQuery('');
    setSearchResults([]);
    setShowCreateProjectModal(false);
  };


  // Debounced search for projects
  // Load all projects when component mounts
  useEffect(() => {
    if (!jeweller?.id) return;
    
    const loadProjects = async () => {
      const q = query(
        collection(db, 'projects'),
        where('jewellerId', '==', jeweller.id),
        // UPDATED: Include both enquiry and project_started statuses
       // where('status', 'in', ['new_enquiry', 'in_conversation', 'project_started', 'project_completed']),
       // orderBy('createdAt', 'desc')
      );
      
      try {
        const querySnapshot = await getDocs(q);
        const allProjects = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllProjects(allProjects);
      } catch (error) {
        console.error("Error loading projects:", error);
      }
    };
    
    loadProjects();
  }, [jeweller?.id]);

// Filter projects based on search
useEffect(() => {
  if (!searchQuery.trim()) {
    setSearchResults([]); // Show nothing when not searching
    return;
  }
  
  const filtered = allProjects.filter(project => 
    project.projectName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.customerInfo?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.customerInfo?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  setSearchResults(filtered.slice(0, 10));
}, [searchQuery, allProjects]);




  const handleLineItemChange = (index, e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const newLineItems = prev.lineItems.map((item, i) => {
        if (i !== index) return item;
        if (name === 'description') {
          return { ...item, [name]: value };
        }
        let newValue = value;
        if (name === 'quantity') {
          newValue = value.replace(/^0+(?=\d)/, '');
          newValue = newValue === '' ? 0 : Number(newValue);
        } else if (name === 'price') {
          newValue = value.replace(/^0+(?=\d)/, '');
          newValue = newValue === '' ? '' : Number(newValue);
        }
        return { ...item, [name]: newValue };
      });
      return { ...prev, lineItems: newLineItems };
    });
  };

  const handleAddLineItem = () => {
    setForm((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, price: 0 }],
    }));
  };

  const handleRemoveLineItem = (index) => {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };
  
  

  
  // Handle custom due date change
  const handleCustomDueDateChange = (e) => {
    setCustomDueDate(e.target.value);
    setForm((prev) => ({ ...prev, dueDate: e.target.value }));
  };
  
  let dueDateNote = '';
  if (dueDateOption !== 'custom') {
    const today = new Date();
    const days = dueDateOption;
    const due = addDays(today, days);
    dueDateNote = `This invoice will be due ${days === 0 ? 'today' : days === 1 ? 'tomorrow' : days + ' days'} after the invoice is finalised or sent (${formatDate(due)} if sent today).`;
  }

  // Main create and send function
  const handleCreateAndSend = async (e) => {
    e.preventDefault(); 
    
    if (isSending) return;
  
    // Simplified validation
    if (!jeweller?.id) {
      alert('Jeweller not found. Please log in again.');
      return;
    }
    if (!selectedProject) {
      alert("Please select or create a project before sending.");
      return;
    }
    if (form.lineItems.length === 0 || !form.lineItems[0].description) {
      alert("Please add at least one line item with a description.");
      return;
    }
  
    setIsSending(true);
  
  try {
      const functions = getFunctions();
      const createInvoiceAndSend = httpsCallable(functions, 'createInvoiceAndSend');
      
      await createInvoiceAndSend({ 
        projectId: selectedProject.id,
        lineItems: form.lineItems,
        dueDate: form.dueDate,
        notes: form.notes
      });
      
      setShowSuccess(true);
      
      setTimeout(() => {
        navigate('/invoices');
      }, 2000);
  
    } catch (error) {
      console.error("Error creating and sending invoice:", error);
      alert(`Failed to send invoice: ${error.message}`);
    } finally {
      setIsSending(false); 
    }
  };

  // Success overlay component
  if (showSuccess) {
    return (
      <div className={styles.successOverlay}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className={styles.successTitle}>Invoice sent successfully</h2>
          <p className={styles.successText}>
            Your invoice has been created and sent to {selectedProject?.customerInfo?.name}.
          </p>
          <p className={styles.helperText}>Redirecting you back to invoices...</p>
          <div className={styles.actionStack}>
            <button 
              onClick={() => navigate('/invoices')}
              className={styles.primaryButton}
            >
              Go to invoices now
            </button>
          </div>
        </div>
      </div>
    );
  }


return (
  <div className={styles.page}>
    <div className={styles.pageHeader}>
      <Link to="/invoices" className={styles.backButton}>
        &larr; Back to invoices
      </Link>
      <h1 className={styles.pageTitle}>Create an Invoice</h1>
    </div>

    <div className={styles.layout}>
      {/* COLUMN 1: The Form */}
      <div className={styles.mainColumn}>
        <div className={styles.card}>
          <div className={styles.stepLabel}>Step 1</div>
          <h2 className={styles.sectionTitle}>Select project</h2>
          <p className={styles.sectionDescription}>
            Send an invoice to one of your customers on an existing project or create a new project
          </p>
          {selectedProject ? (
            <div className={styles.selectionCard}>
              <div className={styles.selectionMain}>
                <div className={styles.selectionHeader}>
                  <span className={styles.projectName}>{selectedProject.projectName}</span>
                  <span className={styles.statusBadge}>
                    {selectedProject.status === 'enquiry' ? 'Enquiry' : 'Project started'}
                  </span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Customer</span>
                  <span className={styles.metaValue}>{selectedProject.customerInfo?.name}</span>
                </div>
                {selectedProject.description && (
                  <p className={styles.helperText}>{selectedProject.description}</p>
                )}
                {selectedProject.status === 'enquiry' && selectedProject.enquiryDetails && (
                  <div className={styles.noteBox}>
                    {selectedProject.enquiryDetails.budget && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaLabel}>Budget</span>
                        <span className={styles.metaValue}>{selectedProject.enquiryDetails.budget}</span>
                      </div>
                    )}
                    {selectedProject.enquiryDetails.timeline && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaLabel}>Timeline</span>
                        <span className={styles.metaValue}>{selectedProject.enquiryDetails.timeline}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Previous invoices</span>
                  <span className={styles.metaValueStrong}>
                    {formatCurrency((selectedProject.financials?.totalInvoicedAmount || 0) / 100)}
                  </span>
                </div>
                {selectedProject.status === 'enquiry' && (
                  <div className={styles.noteBox}>
                    📝 <strong>Note:</strong> Creating an invoice will automatically move this enquiry to
                    {" "}Project Started.
                  </div>
                )}
              </div>
              <div className={styles.buttonRow}>
                <button 
                  type="button" 
                  onClick={() => setSelectedProject(null)} 
                  className={styles.ghostButton}
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.searchBlock}>
              <label className={styles.label} htmlFor="project-search">Search for an existing project</label>
              <input 
                id="project-search"
                type="text" 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className={styles.input} 
                placeholder="Search by project name, customer name, or email"
              />
              
              {searchResults.length > 0 && (
                <div className={styles.projectResults}>
                  {searchResults.map(project => (
                    <div 
                      key={project.id} 
                      onClick={() => setSelectedProject(project)} 
                      className={styles.projectResult}
                    >
                      <div className={styles.projectResultTitle}>{project.projectName}</div>
                      <div className={styles.projectResultMeta}>Customer: {project.customerInfo?.name}</div>
                      <div className={styles.projectResultMeta}>{project.customerInfo?.email}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.buttonRow}>
                <button 
                  type="button" 
                  onClick={() => setShowCreateProjectModal(true)} 
                  className={styles.primaryButton}
                >
                  + Create new project
                </button>
              </div>
            </div>
          )}
        </div>

        {/* STEP 2: Invoice Details - Only show when project selected */}
        {selectedProject && (
          <form onSubmit={handleCreateAndSend} className={styles.card}>
            <div className={styles.stepLabel}>Step 2</div>
            <h2 className={styles.sectionTitle}>Invoice details</h2>
            <p className={styles.sectionDescription}>Set when it is due and what you are billing for.</p>
              
            {/* Due Date Section */}
            <div className={styles.fieldStack}>
              <div>
                <div className={styles.label}>Due date</div>
                <select 
                  value={dueDateOption} 
                  onChange={handleDueDateOptionChange} 
                  className={styles.select}
                >
                  {DUE_DATE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {dueDateOption === 'custom' && (
                  <input 
                    type="date" 
                    value={customDueDate} 
                    onChange={handleCustomDueDateChange} 
                    className={styles.input}
                    required
                  />
                )}
                {dueDateOption !== 'custom' && (
                  <p className={styles.dueDateNote}>{dueDateNote}</p>
                )}
              </div>
            </div>

            {/* Invoice Items */}
            <div className={styles.fieldStack}>
              <div className={styles.badgeLabel}>Line items</div>
              {form.lineItems.map((item, idx) => (
                <div key={idx} className={styles.lineItemCard}>
                  <div className={styles.lineItemHeader}>
                    <span className={styles.lineItemTitle}>Item {idx + 1}</span>
                    <div className={styles.lineItemControls}>
                      <span className={styles.inlineTotal}>
                        {formatCurrency(Number(item.quantity) * Number(item.price || 0))}
                      </span>
                      {form.lineItems.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => handleRemoveLineItem(idx)} 
                          className={styles.removeButton}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className={styles.fieldStack}>
                    <div>
                      <label className={styles.label}>Description</label>
                      <input 
                        type="text" 
                        name="description" 
                        value={item.description} 
                        onChange={e => handleLineItemChange(idx, e)} 
                        required 
                        className={styles.input} 
                        placeholder="What are you billing for?"
                      />
                    </div>
                    <div className={styles.fieldRow}>
                      <div>
                        <label className={styles.label}>Quantity</label>
                        <input 
                          type="number" 
                          name="quantity" 
                          min="1" 
                          value={item.quantity === 0 ? '' : item.quantity} 
                          onChange={e => handleLineItemChange(idx, e)} 
                          required 
                          className={styles.input}
                        />
                      </div>
                      <div>
                        <label className={styles.label}>Price (£)</label>
                        <input 
                          type="number" 
                          name="price" 
                          min="0" 
                          step="0.01" 
                          value={item.price === 0 ? '' : item.price} 
                          onChange={e => handleLineItemChange(idx, e)} 
                          required 
                          className={styles.input} 
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button 
                type="button" 
                onClick={handleAddLineItem} 
                className={`${styles.subtleButton} ${styles.addItemButton}`}
              >
                + Add item
              </button>
            </div>

            {/* Subtotal */}
            <div className={styles.subtotalPanel}>
              <span className={styles.subtotalLabel}>Subtotal</span>
              <span className={styles.subtotalValue}>{formatCurrency(subtotal)}</span>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionStack}>
              <button 
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={styles.ghostButton}
              >
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
              <button 
                type="submit" 
                disabled={isSending} 
                className={styles.primaryButton}
              >
               {isSending ? 'Sending...' : 'Create and send invoice'}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* COLUMN 2: Summaries and Preview (Collapsible) */}
      {selectedProject && showPreview && (
        <div className={styles.sideColumn}>
          <div className={styles.sticky}>
            
            {/* Payout Summary */}
            <div className={`${styles.card} ${styles.summaryCard}`}>
              <div className={`${styles.sectionChip} ${styles.sectionChipPurple}`}>Payout summary</div>
              <PayoutSummary 
                subtotal={subtotal}
                commission={commission}
                processingFee={processingFee}
                totalPlatformFee={totalPlatformFee}
                netPayout={netPayout}
                selectedProject={selectedProject}
                feeBreakdown={fees.breakdown}
                hideTitle
              />
            </div>

            {/* Invoice Preview */}
            <div className={`${styles.card} ${styles.previewCard}`}>
              <div className={`${styles.sectionChip} ${styles.sectionChipPurple}`}>Invoice preview</div>
              <InvoicePreview 
                jeweller={jeweller}
                customer={selectedProject.customerInfo}
                invoiceData={form}
                financials={{ subtotal, commission, netPayout }}
              />
            </div>

          </div>
        </div>
      )}
    </div>

    {/* CREATE PROJECT MODAL */}
    <CreateProjectModal
      isOpen={showCreateProjectModal}
      onClose={() => setShowCreateProjectModal(false)}
      onProjectCreated={handleProjectCreated}
      title="Create Project for Invoice" // Another context
      description="Create a project to attach this invoice to"
    />
  </div>
);
};

export default InvoiceCreator;
